import * as React from 'react';
import { UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { Phrase } from 'components/shared';
import { TextWithA11y } from 'components/shared/text-with-a11y';
import { FlightsBargainJourneySummaryType, FlightsJourneySummaryType, MessageAndAccessibilityType } from 'types/shared';
import { FlightsInformationTextSectionLoaded, FlightsShoppingPwaFlightInformationResults } from '__generated__/api/';
import FlightsJourneyPolicies = FlightsShoppingPwaFlightInformationResults.FlightsJourneyPolicies;
import Messages = FlightsInformationTextSectionLoaded.Messages;

interface JourneySummaryProps {
  flightsJourneySummary: FlightsJourneySummaryType;
  flightsJourneyPolicies?: FlightsJourneyPolicies;
}

export const JourneySummary = ({ flightsJourneySummary, flightsJourneyPolicies }: JourneySummaryProps) => {
  const { heading, __typename, basicFlightDetails, differentDayArrival } = flightsJourneySummary;

  const arrivalDayInformation =
    __typename === 'FlightsBargainJourneySummary' &&
    (flightsJourneySummary as FlightsBargainJourneySummaryType).arrivalDayInformation;

  return (
    <UitkLayoutFlex direction="column" space="one" dataTestId="flight-summary" className="all-x-padding-three">
      <UitkLayoutFlexItem>
        <UitkText size={300} weight="bold" theme="primary">
          <span aria-hidden={true}>{heading.text}</span>
          <span className="is-visually-hidden">{heading.accessibilityMessage}</span>
          {differentDayArrival && (
            <sup
              aria-hidden={true}
              className="uitk-type-100 uitk-type-regular uitk-text-negative-theme all-l-margin-one"
            >
              {differentDayArrival.text}
              <span className="is-visually-hidden">{differentDayArrival.accessibilityMessage}</span>
            </sup>
          )}
        </UitkText>
      </UitkLayoutFlexItem>
      {basicFlightDetails.map((accessibilityMessage: MessageAndAccessibilityType, index: number) => {
        return (
          <UitkLayoutFlexItem key={`basicFlightDetails-${index}`}>
            <TextWithA11y
              messageAndAccessibility={accessibilityMessage}
              theme="primary"
              key={index}
              inline={true}
              size={300}
              headingLevel={2}
              headingSize={300}
            />
          </UitkLayoutFlexItem>
        );
      })}
      {arrivalDayInformation && (
        <UitkLayoutFlexItem>
          <UitkText size={300} inline={true}>
            {arrivalDayInformation}
          </UitkText>
        </UitkLayoutFlexItem>
      )}
      {flightsJourneyPolicies && (
        <div data-test-id="flight-journey-policies" className="all-y-padding-three">
          {flightsJourneyPolicies.messages.map((message: Messages, messageIndex: number) => (
            <UitkLayoutFlexItem key={`journey-policy-${messageIndex}`}>
              <UitkText size={200} spacing="three">
                <Phrase {...message} />
              </UitkText>
            </UitkLayoutFlexItem>
          ))}
        </div>
      )}
    </UitkLayoutFlex>
  );
};
